/** The drawing half of the receipt chain (`receiptLinks.ts` decides; this draws).
 *
 * A vertical rail with one node per link. Every node carries its standing as **an icon and a word**,
 * never colour alone (design rule 4, F-040): present, not applicable, or missing. A missing link is
 * drawn as a visible break — a dashed rail below it, a bordered card, and the reason in words — so
 * an auditor reading down the chain sees where the trail stops instead of a row that is not there.
 *
 * The content of each node is the caller's (`children`): the drawer owns the fields it prints, and
 * this owns only the frame, so the arithmetic exists in one node and one place.
 */

import { CheckCircle, MinusCircle, XCircle } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import { linkStanding, summarise } from "./receiptLinks";
import type { ChainNode, LinkStep, Standing } from "./receiptLinks";
import type { Receipt } from "../api/queries";
import styles from "./ReceiptChain.module.css";

const GLYPH: Record<Standing, Icon> = {
  present: CheckCircle,
  not_applicable: MinusCircle,
  missing: XCircle,
};

const STANDING_WORD: Record<Standing, string> = {
  present: "Present",
  not_applicable: "Not applicable",
  missing: "Missing",
};

/** The server's wording for a link, for the small list inside a node. */
const LINK_WORD: Record<Standing, string> = {
  present: "Met",
  not_applicable: "Not applicable",
  missing: "Not established",
};

export function ReceiptChain({
  nodes,
  children,
}: {
  nodes: readonly ChainNode<string>[];
  /** The `ChainLink`s, in order. */
  children: ReactNode;
}) {
  const counts = summarise(nodes);
  return (
    <section className={styles.chainWrap} aria-label="Evidence chain">
      <p className={styles.intro}>
        Evidence chain — read down from the source document to the ledger entry that recorded the
        figure.{" "}
        <span data-testid="chain-summary">
          {counts.total} links: {counts.present} present, {counts.not_applicable} not applicable,{" "}
          {counts.missing} missing.
        </span>
      </p>
      <ol className={styles.rail} role="list" data-testid="receipt-chain">
        {children}
      </ol>
    </section>
  );
}

export function ChainLink({
  node,
  receipt,
  focused = false,
  children,
}: {
  node: ChainNode<string>;
  /** The calculated receipt, for the small list of the server's evidence links a node answers.
   * Absent for a chain that has none: an imported figure's (`importedChain.ts`) is not assessed by
   * the six-link evidence check, so its nodes carry no `links` and need nothing to look them up in. */
  receipt?: Receipt;
  /** The node an evidence-meter click asked for: scrolled to and marked, once, when it mounts. */
  focused?: boolean;
  children?: ReactNode;
}) {
  const Glyph = GLYPH[node.standing];
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    // `?.` because jsdom has no scrollIntoView; a browser always does.
    if (focused) ref.current?.scrollIntoView?.({ block: "center" });
  }, [focused]);
  return (
    <li
      ref={ref}
      className={styles.node}
      data-focused={focused ? "true" : undefined}
      data-standing={node.standing}
      data-testid={`chain-node-${node.key}`}
    >
      <span className={styles.marker} aria-hidden="true">
        <Glyph size={20} weight="fill" />
      </span>
      <div className={styles.card}>
        <div className={styles.nodeHead}>
          <h3 className={styles.nodeLabel}>{node.label}</h3>
          <span className={styles.standing} data-testid={`chain-standing-${node.key}`}>
            {STANDING_WORD[node.standing]}
          </span>
        </div>
        {node.reason ? (
          <p className={styles.reason} data-testid={`chain-reason-${node.key}`}>
            {node.standing === "missing" ? <strong>The chain breaks here. </strong> : null}
            {node.reason}
          </p>
        ) : null}
        {children}
        {node.links.length > 0 && receipt ? <LinkList links={node.links} receipt={receipt} /> : null}
      </div>
    </li>
  );
}

/** The server's evidence links this node answers, each with its own standing in words. */
function LinkList({ links, receipt }: { links: readonly LinkStep[]; receipt: Receipt }) {
  return (
    <ul className={styles.links} aria-label="Evidence links checked here">
      {links.map((step) => {
        const standing = linkStanding(receipt, step);
        const Glyph = GLYPH[standing];
        return (
          <li
            key={step}
            className={styles.linkItem}
            data-standing={standing}
            data-testid={`chain-link-${step}`}
          >
            <Glyph size={12} weight="bold" aria-hidden="true" />
            {step}: {LINK_WORD[standing]}
          </li>
        );
      })}
    </ul>
  );
}
